"use client";

/**
 * `DateField` — calendar-date input backed by the browser's native
 * `<input type="date">`. The form-state value is always the ISO calendar
 * string `YYYY-MM-DD` that the browser produces; we rely on rhf's plain
 * `register` (no `valueAsDate`) so the canonical form-state stays a
 * primitive string. Strings round-trip cleanly through JSON (autosave,
 * server payloads), don't carry timezone surprises that a `Date` would,
 * and let zod refinements use `z.string().regex(/^\d{4}-\d{2}-\d{2}$/)`
 * directly.
 *
 * Why not `valueAsDate: true`: the browser surfaces a `Date` at midnight
 * UTC for a date input, which means a user in `Europe/Belgrade` who
 * picks `2026-05-02` will get a `Date` whose local representation is
 * still `2026-05-02` but whose `toISOString()` says `2026-05-02T00:00:00Z`.
 * Submitting that to a backend that interprets it as a timestamp can
 * shift the date depending on the server's timezone — a one-day-off bug
 * that haunts every greenfield project until they pick a convention. We
 * pick the convention here: dates are strings.
 *
 * `min` and `max` are passed through as native attributes so the browser's
 * date picker enforces the bounds at pick time. The actual server-side
 * enforcement is the caller's job — the engine just exposes the metadata.
 */

import * as React from "react";
import { Input } from "@aio/platform-ui";
import { useFormContext } from "../form-context";
import type { DateFieldDef } from "../types";
import { FieldShell } from "./field-shell";
import { readErrorMessage } from "./read-error";
import { resolveFieldPath } from "./path";

export interface DateFieldProps {
  field: DateFieldDef;
  /** Path prefix from a parent {@link RepeaterField}. See `text-field.tsx`. */
  pathPrefix?: string;
}

export function DateField({
  field,
  pathPrefix,
}: DateFieldProps): React.ReactElement | null {
  const { form } = useFormContext();

  if (field.condition && !field.condition(form.getValues())) {
    return null;
  }

  const path = resolveFieldPath(field.name, pathPrefix);
  const errorMessage = readErrorMessage(form.formState.errors, path);

  return (
    <FieldShell
      htmlFor={path}
      label={field.label}
      {...(field.description !== undefined ? { description: field.description } : {})}
      {...(errorMessage !== undefined ? { error: errorMessage } : {})}
    >
      <Input
        id={path}
        type="date"
        aria-invalid={errorMessage !== undefined ? true : undefined}
        {...(field.disabled ? { disabled: true } : {})}
        {...(field.placeholder !== undefined
          ? { placeholder: field.placeholder }
          : {})}
        {...(field.min !== undefined ? { min: field.min } : {})}
        {...(field.max !== undefined ? { max: field.max } : {})}
        {...form.register(path)}
      />
    </FieldShell>
  );
}
